import { Controller } from 'stimulus'
import { isPostHogAvailable } from "src/javascripts/posthog-utils.js"

export default class extends Controller {
  static targets = ['analyticsConsent', 'marketingConsent', 'banner', 'preferencesPanel']
  static values = { countryLocale: String, domain: String }

  connect() {
    // Reopen-only instances (e.g. the footer "Cookie settings" link) declare
    // their own controller scope with no banner of their own — they only
    // need the reopen()/togglePreferences() actions, not this connect-time
    // show/consent-sync logic, which the banner's own instance already runs.
    if (!this.hasBannerTarget) return

    const consent = this._readConsent()
    if (!consent) {
      setTimeout(() => {
        this.bannerTarget.classList.remove('hidden')
      }, 500)
    } else {
      this._syncToggles(consent)
      this.updateGoogleConsent()
    }
  }

  updateGoogleConsent() {
    const consent = this._readConsent() || { analytics: false, marketing: false }
    const googleConsent = {
      'ad_storage': consent.marketing ? 'granted' : 'denied',
      'analytics_storage': consent.analytics ? 'granted' : 'denied',
      'ad_user_data': consent.marketing ? 'granted' : 'denied',
      'ad_personalization': consent.marketing ? 'granted' : 'denied'
    }

    try {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', googleConsent)
      }
      if (window.dataLayer) window.dataLayer.push({ event: 'consent_update', consent })
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('Consent apply failed', e)
    }
  }

  accept() {
    this._applyConsent({ analytics: true, marketing: true })
  }

  // The preferences panel's own button: saves the toggles as the visitor set them.
  savePreferences() {
    this._applyConsent({
      analytics: this.hasAnalyticsConsentTarget && this.analyticsConsentTarget.checked,
      marketing: this.hasMarketingConsentTarget && this.marketingConsentTarget.checked
    })
  }

  decline() {
    this._applyConsent({ analytics: false, marketing: false })
  }

  close() { this._closeBanner() }

  // Reopens the banner and preferences panel from outside the banner itself
  // (e.g. the footer "Cookie settings" link). Deliberately queries the DOM
  // directly rather than using bannerTarget/preferencesPanelTarget, since the
  // element triggering this action declares its own independent
  // data-controller="cookies" instance with no banner of its own.
  reopen() {
    document.querySelectorAll('[data-cookies-target="banner"]').forEach((el) => el.classList.remove('hidden'))
    const panel = document.querySelector('[data-cookies-target="preferencesPanel"]')
    if (panel) panel.open = true
  }

  togglePreferences() {
    if (this.hasPreferencesPanelTarget) this.preferencesPanelTarget.open = !this.preferencesPanelTarget.open
  }

  // helpers
  _applyConsent(consent) {
    this._syncToggles(consent)
    this._persistConsent(consent)
    this.updateGoogleConsent()

    try {
      if (consent.analytics && isPostHogAvailable() && typeof posthog !== 'undefined') {
        posthog.opt_in_capturing?.()
        posthog.capture?.('user_opted_in', {
          $set: { consent_given: true, opt_in_date: new Date().toISOString(), country_locale: this.countryLocaleValue }
        })
        posthog.people?.set?.({ country_locale: this.countryLocaleValue })
      }
    } catch (e) { console.warn('PostHog consent update failed', e) }

    try {
      if (typeof mixpanel !== 'undefined') {
        if (consent.analytics) {
          mixpanel.opt_in_tracking?.()
        } else {
          mixpanel.opt_out_tracking?.()
        }
      }
    } catch (e) { console.warn('Mixpanel consent update failed', e) }

    this._closeBanner()
  }

  // The panel shows the saved choice when reopened, and nothing ticked before one.
  _syncToggles(consent) {
    if (this.hasAnalyticsConsentTarget) this.analyticsConsentTarget.checked = consent.analytics
    if (this.hasMarketingConsentTarget) this.marketingConsentTarget.checked = consent.marketing
  }

  _persistConsent(consent) {
    const domain = this._sharedCookieDomain()
    this._setCookie('wg_consent', JSON.stringify({
      version: 2,
      timestamp: new Date().toISOString(),
      domain,
      functional: true,
      analytics: consent.analytics,
      marketing: consent.marketing
    }), domain)
    this._setCookie('site_consent', JSON.stringify(consent))
    this._setCookie('accepted_cookies', consent.analytics && consent.marketing ? 'yes' : 'no')
    window.wgPosthogConsent?.sync?.()
  }

  _setCookie(name, value, domain = '') {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
    const domainAttribute = domain ? `; domain=${domain}` : ''
    const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${domainAttribute}${secureAttribute}`
  }

  _readConsent() {
    const shared = this._readCookie('wg_consent')
    if (shared?.version !== 2 || shared.domain !== this._sharedCookieDomain()) return null
    if (typeof shared.analytics !== 'boolean' || typeof shared.marketing !== 'boolean') return null

    return {
      analytics: shared.analytics,
      marketing: shared.marketing
    }
  }

  _readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`))
    if (!match) return null
    try { return JSON.parse(decodeURIComponent(match[1])) } catch { return null }
  }

  // The configured consent domain (COOKIE_DOMAIN, via the banner's
  // data-cookies-domain-value), used only when this host belongs to it, as the
  // marketing site's getEffectiveCookieDomain does. Reopen-only instances have
  // no value and fall back to production.
  _sharedCookieDomain() {
    const configured = this.hasDomainValue && this.domainValue ? this.domainValue : '.wearewildgoose.com'
    const bare = configured.replace(/^\./, '')
    const hostname = window.location.hostname
    return hostname === bare || hostname.endsWith(`.${bare}`) ? configured : ''
  }

  _closeBanner() { this.bannerTarget?.classList.add('hidden') }
}
